import type { Stint, StintStats, PlayerRotation } from './types';

// Team colors for accents
export const TEAM_COLORS: Record<string, string> = {
  ATL: '#E03A3E', BKN: '#000000', BOS: '#007A33', CHA: '#1D1160', CHI: '#CE1141',
  CLE: '#860038', DAL: '#00538C', DEN: '#0E2240', DET: '#C8102E', GSW: '#1D428A',
  HOU: '#CE1141', IND: '#002D62', LAC: '#C8102E', LAL: '#552583', MEM: '#5D76A9',
  MIA: '#98002E', MIL: '#00471B', MIN: '#0C2340', NOP: '#0C2340', NYK: '#006BB6',
  OKC: '#007AC1', ORL: '#0077C0', PHI: '#006BB6', PHX: '#1D1160', POR: '#E03A3E',
  SAC: '#5A2D81', SAS: '#C4CED4', TOR: '#CE1141', UTA: '#002B5C', WAS: '#002B5C',
};

// Accessible color palette for plus/minus (gold/steel instead of red/green)
export const STINT_COLORS = {
  excellent: '#FFD700',    // +10 or more (gold)
  good: '#DAA520',         // +5 to +9
  positive: '#B8860B',     // +1 to +4
  neutral: 'rgba(148, 163, 184, 0.6)', // 0
  slight: '#6B8299',       // -1 to -4 (steel)
  poor: '#4A6178',         // -5 to -9
  terrible: '#2D3D50',     // -10 or worse
} as const;

export const getStintColor = (plusMinus: number): string => {
  if (plusMinus >= 10) return STINT_COLORS.excellent;
  if (plusMinus >= 5) return STINT_COLORS.good;
  if (plusMinus > 0) return STINT_COLORS.positive;
  if (plusMinus === 0) return STINT_COLORS.neutral;
  if (plusMinus >= -4) return STINT_COLORS.slight;
  if (plusMinus >= -9) return STINT_COLORS.poor;
  return STINT_COLORS.terrible;
};

// Ensure colors are visible on dark backgrounds
export const ensureVisibleColor = (hex: string, minLuminance: number = 0.15): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  if (luminance >= minLuminance) return hex;

  const factor = minLuminance / Math.max(luminance, 0.01);
  const newR = Math.min(255, Math.round(r * factor + (255 - r * factor) * 0.3));
  const newG = Math.min(255, Math.round(g * factor + (255 - g * factor) * 0.3));
  const newB = Math.min(255, Math.round(b * factor + (255 - b * factor) * 0.3));

  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

// Format game time (seconds elapsed) to game-clock format (counts down from 12:00).
// Pass isEndTime=true for end-of-range values so period boundaries display as
// "Q4 0:00" instead of "OT1 12:00".
export const formatGameTime = (seconds: number, isEndTime?: boolean): string => {
  if (isEndTime && seconds > 0 && seconds % 720 === 0) {
    const period = seconds / 720;
    const periodLabel = period <= 4 ? `Q${period}` : `OT${period - 4}`;
    return `${periodLabel} 0:00`;
  }
  const period = Math.floor(seconds / 720) + 1;
  const periodElapsed = seconds % 720;
  const remaining = 720 - periodElapsed;
  const minutes = Math.floor(remaining / 60);
  const secs = Math.round(remaining % 60);
  const periodLabel = period <= 4 ? `Q${period}` : `OT${period - 4}`;
  return `${periodLabel} ${minutes}:${secs.toString().padStart(2, '0')}`;
};

// Format duration in seconds to "M:SS"
export const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Format plus/minus with sign
export const formatPM = (pm: number): string => (pm > 0 ? `+${pm}` : `${pm}`);

// Format player name - abbreviate if too long
export const formatPlayerName = (fullName: string): string => {
  if (fullName.length <= 18) return fullName;
  const parts = fullName.split(' ');
  if (parts.length < 2) return fullName;

  const suffixes = ['Jr.', 'Jr', 'Sr.', 'Sr', 'II', 'III', 'IV', 'V'];
  const lastPart = parts[parts.length - 1];
  const hasSuffix = suffixes.includes(lastPart);

  if (hasSuffix && parts.length > 2) {
    return `${parts[0][0]}. ${parts[parts.length - 2]} ${lastPart}`;
  }

  return `${parts[0][0]}. ${parts[parts.length - 1]}`;
};

const NAME_SUFFIXES = new Set(['jr', 'jr.', 'sr', 'sr.', 'ii', 'iii', 'iv', 'v']);

export const getLastName = (fullName: string): string => {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 1) return fullName;
  const last = parts[parts.length - 1];
  if (parts.length > 2 && NAME_SUFFIXES.has(last.toLowerCase())) {
    return `${parts[parts.length - 2]} ${last}`;
  }
  return last;
};

// Stat display colors
export const STAT_COLORS: Record<string, string> = {
  PTS: '#F59E0B', // amber
  REB: '#3B82F6', // blue
  AST: '#10B981', // green
  STL: '#8B5CF6', // purple
  BLK: '#06B6D4', // cyan
  TOV: '#EF4444', // red
};

export const hasAnyStats = (stats: StintStats): boolean =>
  stats.fga > 0 || stats.fg3a > 0 || stats.fta > 0 ||
  stats.reb > 0 || stats.ast > 0 || stats.stl > 0 ||
  stats.blk > 0 || stats.tov > 0;

// UI chrome colors used across rotation components
export const ROTATION_COLORS = {
  bg: { card: '#1a1d24', container: '#242830', deep: '#14171C', page: '#000A16' },
  border: { default: '#3a3f4b', subtle: 'rgba(58, 63, 75, 0.3)', faint: 'rgba(58, 63, 75, 0.15)' },
  text: { primary: '#E8EAED', secondary: '#8B95A5', muted: '#6B7280' },
  pm: { positive: '#4ade80', negative: '#f87171', neutral: '#8B95A5' },
  accent: { teal: '#4a9f9d', cyan: '#22D3EE', blue: '#60A5FA', lightBlue: '#93C5FD' },
} as const;

export interface TeammateOverlap {
  name: string;
  overlapSeconds: number;
}

/** Compute overlap durations between a stint and all players in a list. */
export function computeOverlaps(
  stint: Stint,
  playerName: string,
  players: PlayerRotation[],
): TeammateOverlap[] {
  const overlaps: TeammateOverlap[] = [];

  for (const otherPlayer of players) {
    if (otherPlayer.playerName === playerName) continue;

    let totalOverlap = 0;
    for (const otherStint of otherPlayer.stints) {
      const overlapStart = Math.max(otherStint.startTime, stint.startTime);
      const overlapEnd = Math.min(otherStint.endTime, stint.endTime);
      if (overlapEnd > overlapStart) {
        totalOverlap += overlapEnd - overlapStart;
      }
    }
    if (totalOverlap > 0) {
      overlaps.push({ name: otherPlayer.playerName, overlapSeconds: totalOverlap });
    }
  }

  overlaps.sort((a, b) => b.overlapSeconds - a.overlapSeconds);
  return overlaps;
}
